// ─── Airtable Client ───────────────────────────────────────────────────────────
const TOKEN = import.meta.env.VITE_AIRTABLE_TOKEN as string;
const BASE_ID = import.meta.env.VITE_AIRTABLE_BASE_ID as string;
const API = `https://api.airtable.com/v0/${BASE_ID}`;

const headers = () => ({
  Authorization: `Bearer ${TOKEN}`,
  'Content-Type': 'application/json',
});

// Table names
export const TABLES = {
  LEADS: 'Leads',
  INSTALADORES: 'Instaladores',
  INSTALADORES_COMELIT: 'InstalaldoresComelit',
  PEDIDOS_ORCAMENTO: 'PedidosOrcamento',
  PEDIDOS_EXTENSAO: 'PedidosExtensao',
} as const;

type AirtableRecord = { id: string; fields: Record<string, unknown> };

// ─── GET all records (handles pagination) ─────────────────────────────────────
export async function atGetAll(table: string): Promise<AirtableRecord[]> {
  const records: AirtableRecord[] = [];
  let offset: string | undefined;
  do {
    const url = new URL(`${API}/${encodeURIComponent(table)}`);
    if (offset) url.searchParams.set('offset', offset);
    const res = await fetch(url.toString(), { headers: headers() });
    if (!res.ok) throw new Error(`Airtable GET error: ${res.status}`);
    const data = await res.json() as { records: AirtableRecord[]; offset?: string };
    records.push(...data.records);
    offset = data.offset;
  } while (offset);
  return records;
}

// ─── GET single record by our UUID field "lid" ────────────────────────────────
export async function atGetByLid(table: string, lid: string): Promise<AirtableRecord | null> {
  const url = new URL(`${API}/${encodeURIComponent(table)}`);
  url.searchParams.set('filterByFormula', `{lid}="${lid}"`);
  const res = await fetch(url.toString(), { headers: headers() });
  if (!res.ok) return null;
  const data = await res.json() as { records: AirtableRecord[] };
  return data.records[0] ?? null;
}

// ─── CREATE ───────────────────────────────────────────────────────────────────
export async function atCreate(table: string, fields: Record<string, unknown>): Promise<AirtableRecord> {
  const res = await fetch(`${API}/${encodeURIComponent(table)}`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) throw new Error(`Airtable CREATE error: ${res.status} ${await res.text()}`);
  return res.json() as Promise<AirtableRecord>;
}

// ─── UPDATE by Airtable record ID ────────────────────────────────────────────
export async function atUpdate(table: string, recordId: string, fields: Record<string, unknown>): Promise<void> {
  const res = await fetch(`${API}/${encodeURIComponent(table)}/${recordId}`, {
    method: 'PATCH',
    headers: headers(),
    body: JSON.stringify({ fields }),
  });
  if (!res.ok) throw new Error(`Airtable UPDATE error: ${res.status}`);
}

// ─── UPDATE by lid (finds record first) ──────────────────────────────────────
export async function atUpdateByLid(table: string, lid: string, fields: Record<string, unknown>): Promise<void> {
  const rec = await atGetByLid(table, lid);
  if (!rec) throw new Error(`Record not found: lid=${lid}`);
  await atUpdate(table, rec.id, fields);
}

// ─── DELETE by lid ────────────────────────────────────────────────────────────
export async function atDeleteByLid(table: string, lid: string): Promise<void> {
  const rec = await atGetByLid(table, lid);
  if (!rec) return;
  const res = await fetch(`${API}/${encodeURIComponent(table)}/${rec.id}`, {
    method: 'DELETE',
    headers: headers(),
  });
  if (!res.ok) throw new Error(`Airtable DELETE error: ${res.status}`);
}

// ─── Field mappers ────────────────────────────────────────────────────────────
// These convert between our internal types and Airtable field format

import type { Lead, Instalador, InstaladorComelit, PedidoOrcamento, PedidoExtensaoGarantia, RespostaOrcamento } from './index';

export function leadToFields(l: Lead): Record<string, unknown> {
  return {
    lid: l.id,
    criadoEm: l.criadoEm,
    nome: l.nome,
    email: l.email,
    telefone: l.telefone,
    morada: l.morada,
    localidade: l.localidade,
    codigoPostal: l.codigoPostal,
    tipoImovel: l.tipoImovel,
    dataVisita: l.dataVisita,
    notas: l.notas ?? '',
    estado: l.estado,
    instaladorId: l.instaladorId ?? '',
    instaladorIds: JSON.stringify(l.instaladorIds ?? []),
    numeroFatura: l.numeroFatura ?? '',
    notasInternas: l.notasInternas ?? '',
    dataConclusao: l.dataConclusao ?? '',
    sinalPago: l.sinalPago ?? false,
    sinalPagoData: l.sinalPagoData ?? '',
    pagamentoFinalPago: l.pagamentoFinalPago ?? false,
    pagamentoFinalData: l.pagamentoFinalData ?? '',
    instaladorPago: l.instaladorPago ?? false,
    instaladorPagoData: l.instaladorPagoData ?? '',
  };
}

export function fieldsToLead(f: Record<string, unknown>): Lead {
  let instaladorIds: string[] = [];
  try { instaladorIds = JSON.parse((f.instaladorIds as string) || '[]'); } catch { instaladorIds = []; }
  return {
    id: f.lid as string,
    criadoEm: f.criadoEm as string,
    nome: f.nome as string,
    email: f.email as string,
    telefone: f.telefone as string,
    morada: f.morada as string,
    localidade: f.localidade as string,
    codigoPostal: f.codigoPostal as string,
    tipoImovel: f.tipoImovel as Lead['tipoImovel'],
    dataVisita: f.dataVisita as string,
    notas: (f.notas as string) || undefined,
    estado: f.estado as Lead['estado'],
    instaladorId: (f.instaladorId as string) || undefined,
    instaladorIds: instaladorIds.length > 0 ? instaladorIds : undefined,
    numeroFatura: (f.numeroFatura as string) || undefined,
    notasInternas: (f.notasInternas as string) || undefined,
    dataConclusao: (f.dataConclusao as string) || undefined,
    sinalPago: (f.sinalPago as boolean) || false,
    sinalPagoData: (f.sinalPagoData as string) || undefined,
    pagamentoFinalPago: (f.pagamentoFinalPago as boolean) || false,
    pagamentoFinalData: (f.pagamentoFinalData as string) || undefined,
    instaladorPago: (f.instaladorPago as boolean) || false,
    instaladorPagoData: (f.instaladorPagoData as string) || undefined,
  };
}

export function instaladorToFields(i: Instalador): Record<string, unknown> {
  return { lid: i.id, nome: i.nome, zona: i.zona, telefone: i.telefone, email: i.email };
}

export function fieldsToInstalador(f: Record<string, unknown>): Instalador {
  return {
    id: f.lid as string,
    nome: f.nome as string,
    zona: f.zona as string,
    telefone: (f.telefone as string) ?? '',
    email: (f.email as string) ?? '',
  };
}

export function instaladorComelitToFields(i: InstaladorComelit): Record<string, unknown> {
  return {
    lid: i.id,
    criadoEm: i.criadoEm,
    nome: i.nome,
    email: i.email,
    password: i.password,
    telefone: i.telefone,
    nif: i.nif,
    zona: i.zona,
    morada: i.morada,
    temSeguro: i.temSeguro,
    numeroClienteContera: i.numeroClienteContera,
    status: i.status,
    notasContera: i.notasContera ?? '',
    proximaCertificacao: i.proximaCertificacao ?? '',
  };
}

export function fieldsToInstaladorComelit(f: Record<string, unknown>): InstaladorComelit {
  return {
    id: f.lid as string,
    criadoEm: f.criadoEm as string,
    nome: f.nome as string,
    email: f.email as string,
    password: f.password as string,
    telefone: f.telefone as string,
    nif: f.nif as string,
    zona: f.zona as string,
    morada: f.morada as string,
    temSeguro: (f.temSeguro as boolean) ?? false,
    numeroClienteContera: (f.numeroClienteContera as string) ?? '',
    status: f.status as InstaladorComelit['status'],
    notasContera: (f.notasContera as string) || undefined,
    proximaCertificacao: (f.proximaCertificacao as string) || undefined,
  };
}

export function pedidoOrcamentoToFields(p: PedidoOrcamento): Record<string, unknown> {
  return {
    lid: p.id,
    criadoEm: p.criadoEm,
    leadId: p.leadId,
    zona: p.zona,
    descricao: p.descricao,
    estado: p.estado,
    respostasJson: JSON.stringify(p.respostas),
  };
}

export function fieldsToPedidoOrcamento(f: Record<string, unknown>): PedidoOrcamento {
  let respostas: RespostaOrcamento[] = [];
  try { respostas = JSON.parse((f.respostasJson as string) || '[]'); } catch { respostas = []; }
  return {
    id: f.lid as string,
    criadoEm: f.criadoEm as string,
    leadId: f.leadId as string,
    zona: f.zona as string,
    descricao: f.descricao as string,
    estado: f.estado as PedidoOrcamento['estado'],
    respostas,
  };
}

export function pedidoExtensaoToFields(p: PedidoExtensaoGarantia): Record<string, unknown> {
  return { lid: p.id, criadoEm: p.criadoEm, leadId: p.leadId, motivo: p.motivo, estado: p.estado };
}

export function fieldsToPedidoExtensao(f: Record<string, unknown>): PedidoExtensaoGarantia {
  return {
    id: f.lid as string,
    criadoEm: f.criadoEm as string,
    leadId: f.leadId as string,
    motivo: f.motivo as string,
    estado: f.estado as PedidoExtensaoGarantia['estado'],
  };
}

// ── Propostas ──────────────────────────────────────────────────────────────
export interface Proposta {
  id: string;
  recId?: string;
  refCTR: string;
  instaladorId: string;
  instaladorNome: string;
  instaladorEmail?: string;
  clienteNome: string;
  obraMorada: string;
  totalEuros: number;
  htmlContent: string;
  criadoEm: string;
  tipoSistema: string;
  numInquilinos: string;
  estadoCliente?: string;
  dataInstalacao?: string;
}

export function propostaToFields(p: Omit<Proposta,'id'>): Record<string,unknown> {
  return { lid: p.refCTR + '_' + Date.now(), refCTR: p.refCTR, instaladorId: p.instaladorId, instaladorNome: p.instaladorNome, instaladorEmail: p.instaladorEmail || '', clienteNome: p.clienteNome, obraMorada: p.obraMorada, totalEuros: p.totalEuros, htmlContent: p.htmlContent, criadoEm: p.criadoEm, tipoSistema: p.tipoSistema, numInquilinos: p.numInquilinos, estadoCliente: p.estadoCliente || 'pendente', dataInstalacao: p.dataInstalacao || '' };
}

export function fieldsToProposta(f: Record<string,unknown>, recId?: string): Proposta {
  return { id: (f.lid || f.id) as string, recId, refCTR: f.refCTR as string, instaladorId: f.instaladorId as string, instaladorNome: f.instaladorNome as string, instaladorEmail: f.instaladorEmail as string || '', clienteNome: f.clienteNome as string, obraMorada: f.obraMorada as string, totalEuros: Number(f.totalEuros||0), htmlContent: f.htmlContent as string, criadoEm: f.criadoEm as string, tipoSistema: f.tipoSistema as string, numInquilinos: f.numInquilinos as string, estadoCliente: f.estadoCliente as string || 'pendente', dataInstalacao: f.dataInstalacao as string || '' };
}

// ── EmailQueue: cria registo → Airtable Automation envia o email ────────────
// Campos necessários na tabela "EmailQueue" do Airtable:
//   email (Email), nome (Text), assunto (Text), mensagem (Long text)
// Automation: "When record created" → "Send email" to {email}
export async function queueEmail(params: {
  email: string;
  nome:   string;
  assunto: string;
  mensagem: string;
}): Promise<void> {
  try {
    await atCreate('EmailQueue', {
      email:    params.email,
      nome:     params.nome,
      assunto:  params.assunto,
      mensagem: params.mensagem,
    });
  } catch (e) {
    console.error('queueEmail error:', e);
  }
}
